import {
    CampaignOutlined as CampaignOutlinedIcon,
    Loader2Icon,
    MoreVert as MoreVertIcon,
    Sync as SyncIcon,
} from 'components/icons';
import PermissionButton from 'components/buttons/PermissionButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftRowMenu } from 'components/drafts/DraftRecordActions';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { useOpenCreateUrl } from 'components/create-dialog/CreateDialogContext';
import MarketingIntegrationLink from 'components/marketingintegrationlink/MarketingIntegrationLink';
import { FormattedDate } from 'components/misc/FormattedDate';
import { MissingValue } from 'components/misc/MissingValue';
import KebabMenu, { MenuItem as KebabMenuItem } from 'components/navigation/KebabMenu';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { RecordLink } from 'components/record-chip/RecordLink';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    APIMarketingProfilesSlice,
    useGetMarketingProfileSyncOutcomeQuery,
    useLazyGetMarketingProfilesCountQuery,
    useLazyGetMarketingProfilesWithFiltersQuery,
    useTriggerMarketingProfileSyncMutation,
} from 'store/api/marketingProfiles';
import { useIsSmailyIntegrationEnabledQuery } from 'store/api/smaily-integration';
import { MarketingProfileByEmployee, MarketingProfileListItem } from 'types/marketingProfiles';
import { errorAlert, infoAlert, successAlert, warningAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';

const CREATE_PERMISSIONS = ['CREATE.MARKETING_PROFILE'];

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

function EmployeeCell({ value }: { value?: MarketingProfileByEmployee }) {
    if (!value?.name) return <MissingValue />;
    if (value.id) {
        return <RecordLink withOrigin className={LINK_CLASS} to={`/employee/${value.id}`} label={value.name} />;
    }
    return <>{value.name}</>;
}

const SYNC_POLL_INTERVAL_MS = 2000;
const SYNC_POLL_TIMEOUT_MS = 5_000;

const FILTER_CONFIG: FilterConfig = {
    quickFilters: [],
    filters: [],
    searchBarSelectors: [{ selector: 'name' }],
};

interface SyncCellProps {
    profileId: number;
}

const SyncCell = ({ profileId }: SyncCellProps) => {
    const dispatch = useDispatch();
    const [triggerSync, { isLoading: isTriggering }] = useTriggerMarketingProfileSyncMutation();
    const [watchingExecutionId, setWatchingExecutionId] = useState<number | null>(null);
    const timeoutHandleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const stopWatching = useCallback(() => {
        if (timeoutHandleRef.current !== null) {
            clearTimeout(timeoutHandleRef.current);
            timeoutHandleRef.current = null;
        }
        setWatchingExecutionId(null);
    }, []);

    useEffect(
        () => () => {
            if (timeoutHandleRef.current !== null) clearTimeout(timeoutHandleRef.current);
        },
        []
    );

    const { data: outcome, error: outcomeError } = useGetMarketingProfileSyncOutcomeQuery(
        { profileId, executionId: watchingExecutionId ?? 0 },
        { skip: watchingExecutionId === null, pollingInterval: SYNC_POLL_INTERVAL_MS }
    );

    useEffect(() => {
        if (watchingExecutionId === null) return;
        if (outcomeError) {
            errorAlert(t('marketing-profiles.sync-error'));
            queueMicrotask(stopWatching);
            return;
        }
        if (!outcome || outcome.status === 'PENDING') return;
        if (outcome.status === 'PROCESSED') {
            successAlert(t('marketing-profiles.sync-completed'));
            dispatch(APIMarketingProfilesSlice.util.invalidateTags(['MarketingProfiles']));
        } else {
            errorAlert(outcome.errorMessage ?? t('marketing-profiles.sync-error'));
        }
        queueMicrotask(stopWatching);
    }, [outcome, outcomeError, watchingExecutionId, dispatch, stopWatching]);

    const handleClick = useCallback(async () => {
        try {
            const { executionId } = await triggerSync(profileId).unwrap();
            setWatchingExecutionId(executionId);
            timeoutHandleRef.current = setTimeout(() => {
                infoAlert(t('marketing-profiles.sync-started'));
                dispatch(APIMarketingProfilesSlice.util.invalidateTags(['MarketingProfiles']));
                setWatchingExecutionId(null);
                timeoutHandleRef.current = null;
            }, SYNC_POLL_TIMEOUT_MS);
        } catch (err) {
            const status =
                err && typeof err === 'object' && 'status' in err ? (err as { status: number }).status : null;
            const data = (err as { data?: { message?: string } } | undefined)?.data;
            if (status === 409) {
                warningAlert(t('marketing-profiles.sync-already-in-progress'));
            } else if (status === 400) {
                errorAlert(data?.message ?? t('marketing-profiles.sync-error'));
            } else {
                errorAlert(t('marketing-profiles.sync-error'));
            }
        }
    }, [triggerSync, profileId, dispatch]);

    const showSpinner = isTriggering || watchingExecutionId !== null;

    return (
        <PermissionButton
            permissions={['UPDATE.MARKETING_PROFILE']}
            data-testid={`sync-button-${profileId}`}
            variant="outlined"
            size="small"
            disabled={showSpinner}
            onClick={() => {
                void handleClick();
            }}
            startIcon={
                showSpinner ? (
                    <Loader2Icon className="size-[14px] animate-spin" />
                ) : (
                    <SyncIcon className="text-[14px]" />
                )
            }
        >
            {t('marketing-profiles.sync-action')}
        </PermissionButton>
    );
};

const getMarketingProfileHref = (profile: MarketingProfileListItem) => `/contacts/marketing/${profile.id}`;

const MarketingProfilesTable = () => {
    const navigate = useNavigate();
    const isNewDesign = useIsNewDesign();
    // A new profile and a duplicate open in the create dialog, over this list, where there is one.
    const openCreateUrl = useOpenCreateUrl();
    const hasCreatePermission = useHasPermissions(CREATE_PERMISSIONS);
    const [fetchProfiles, { data, isFetching }] = useLazyGetMarketingProfilesWithFiltersQuery();
    const fetchData = useWithDrafts(fetchProfiles);
    const { data: smailyEnabled } = useIsSmailyIntegrationEnabledQuery();

    const columns = useMemo<DataTableColumnDef<MarketingProfileListItem>[]>(() => {
        const baseColumns: DataTableColumnDef<MarketingProfileListItem>[] = [
            {
                header: t('marketing-profiles.profile-name'),
                accessorKey: 'name',
                enableSorting: false,
                meta: {
                    widthWeight: 1.17,
                    minPx: 150,
                },
                cell: ({ row }) => (
                    <div className="flex flex-col items-start gap-1">
                        {row.original.isDraft ? <DraftChip /> : null}
                        {/* A draft may have no name yet. */}
                        {isNewDesign ? (
                            <LinkWithOrigin
                                className={`${LINK_CLASS} font-semibold`}
                                to={getMarketingProfileHref(row.original)}
                            >
                                {row.original.name || t('marketing-profiles.new-profile')}
                            </LinkWithOrigin>
                        ) : (
                            <button
                                type="button"
                                className={`${LINK_CLASS} cursor-pointer text-left font-semibold`}
                                onClick={() => navigate(`/contacts/marketing/${row.original.id}`)}
                            >
                                {row.original.name || t('marketing-profiles.new-profile')}
                            </button>
                        )}
                        {smailyEnabled && row.original.marketingIntegrationLink && (
                            <MarketingIntegrationLink
                                href={row.original.marketingIntegrationLink}
                                integrationName="Smaily"
                                className="self-start"
                            />
                        )}
                    </div>
                ),
            },
            {
                header: t('marketing-profiles.created-at'),
                accessorKey: 'createdAt',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 110,
                },
                cell: ({ getValue }) => <FormattedDate value={getValue()} withTime fallback={<MissingValue />} />,
            },
            {
                header: t('marketing-profiles.created-by'),
                accessorKey: 'createdBy',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 110,
                },
                cell: ({ getValue }) => <EmployeeCell value={getValue()} />,
            },
            {
                header: t('marketing-profiles.modified-at'),
                accessorKey: 'lastModifiedAt',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 110,
                },
                cell: ({ getValue }) => <FormattedDate value={getValue()} withTime fallback={<MissingValue />} />,
            },
            {
                header: t('marketing-profiles.modified-by'),
                accessorKey: 'lastModifiedBy',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 110,
                },
                cell: ({ getValue }) => <EmployeeCell value={getValue()} />,
            },
        ];

        const syncColumns: DataTableColumnDef<MarketingProfileListItem>[] = smailyEnabled
            ? [
                  {
                      header: t('marketing-profiles.synced-at'),
                      accessorKey: 'lastSyncedAt',
                      enableSorting: false,
                      meta: {
                          widthWeight: 1,
                          minPx: 110,
                      },
                      cell: ({ getValue }) => <FormattedDate value={getValue()} withTime fallback={<MissingValue />} />,
                  },
                  {
                      header: t('marketing-profiles.synced-by'),
                      accessorKey: 'lastSyncedBy',
                      enableSorting: false,
                      meta: {
                          widthWeight: 1,
                          minPx: 110,
                      },
                      cell: ({ getValue }) => <EmployeeCell value={getValue()} />,
                  },
                  {
                      header: '',
                      id: 'sync-action',
                      enableSorting: false,
                      meta: {
                          // Button column: sized to its content, not a share of the flex pool.
                          fixedPx: 180,
                      },
                      // A draft is synced once it is created.
                      cell: ({ row }) => (row.original.isDraft ? null : <SyncCell profileId={row.original.id} />),
                  },
              ]
            : [];

        const actionsColumn: DataTableColumnDef<MarketingProfileListItem>[] = hasCreatePermission
            ? [
                  {
                      header: '',
                      id: 'actions',
                      enableSorting: false,
                      meta: {
                          fixedPx: 60,
                      },
                      cell: ({ row }) => {
                          if (row.original.isDraft) {
                              return <DraftRowMenu draft={{ type: 'marketing-profile', id: row.original.id }} />;
                          }
                          const menuItems: KebabMenuItem[] = [
                              {
                                  title: t('marketing-profiles.duplicate-profile-action'),
                                  handler: () => openCreateUrl(`/contacts/marketing/duplicate/${row.original.id}`),
                              },
                          ];

                          return (
                              <KebabMenu
                                  menuItems={menuItems}
                                  icon={<MoreVertIcon fontSize="small" />}
                                  idSuffix={row.original.id}
                              />
                          );
                      },
                  },
              ]
            : [];

        return [...baseColumns, ...syncColumns, ...actionsColumn];
    }, [navigate, openCreateUrl, smailyEnabled, hasCreatePermission, isNewDesign]);

    return (
        <ListTableWithFilters<MarketingProfileListItem>
            getRowHref={getMarketingProfileHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="marketing-profiles"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="marketing-profiles.profiles"
            singularTextId="marketing-profiles.profile"
            noEntriesId="marketing-profiles.no-profiles-found"
            noEntriesBodyId="marketing-profiles.empty.body"
            noEntriesIcon={CampaignOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.MARKETING_PROFILE']}
                    variant="contained"
                    color="secondary"
                    onClick={() => openCreateUrl('/contacts/marketing/new')}
                    labelId="marketing-profiles.new-profile"
                    newLabelId="marketing-profiles.new-profile"
                />
            }
            fetchData={fetchData}
            fetchCount={useLazyGetMarketingProfilesCountQuery}
            filterConfig={FILTER_CONFIG}
            rowsPerPage={50}
            canRememberFilters={false}
        />
    );
};

export default MarketingProfilesTable;
